import { APP_CONFIG } from '@/constants/app';
import { tokenManager } from '@/utils/token-manager';
import axios from 'axios';
import type {
  LeaveCounting,
  LeaveRequest,
  PersonalOverview,
  RestoredPaidLeaveOverlap,
  SirhMe,
  StateMachine,
  TransmissionCheck,
  VariablePeriod,
} from '../types';

/**
 * C3 — Accès à l'API SIRH.
 *
 * Aucune règle métier ici. Le décompte des jours, les transitions permises et
 * le périmètre de visibilité viennent tous du serveur : le portail historique
 * calculait les jours de congés dans le navigateur, ce qui revenait à laisser
 * l'utilisateur décider de son solde.
 */
const sirhApi = axios.create({
  baseURL: APP_CONFIG.api.baseUrl,
  headers: { 'Content-Type': 'application/json' },
});

tokenManager.setupInterceptors(sirhApi);

/**
 * L'API enveloppe toutes ses réponses dans `{ data, meta }`
 * (`TransformInterceptor` du socle). On déballe ici, une fois, plutôt que dans
 * chaque écran : oublier le déballage à un seul endroit produit un écran blanc.
 */
function unwrap<T>(payload: { data?: T } | T): T {
  return payload && typeof payload === 'object' && 'data' in payload
    ? (payload as { data: T }).data
    : (payload as T);
}

export interface LeaveRequestPayload {
  employmentUuid: string;
  leaveTypeUuid: string;
  startsOn: string;
  endsOn: string;
  startBoundary?: 'journee' | 'apres_midi';
  endBoundary?: 'journee' | 'matin';
  employeeComment?: string;
  justificationReference?: string;
}

export const sirhService = {
  /** Qui suis-je pour le SIRH aujourd'hui : mes emplois, ceux que j'encadre. */
  me: async (onDate?: string): Promise<SirhMe> => {
    const response = await sirhApi.get<SirhMe>('/sirh/org/me', {
      params: onDate ? { onDate } : undefined,
    });
    return unwrap(response.data);
  },

  /** Tout l'écran salarié en une requête : demandes, compteurs, types d'absence. */
  mine: async (): Promise<PersonalOverview> => {
    const response = await sirhApi.get<PersonalOverview>('/sirh/leave/mine');
    return unwrap(response.data);
  },

  /** La machine à états, telle que le serveur la définit. L'écran n'en code aucune règle. */
  stateMachine: async (): Promise<StateMachine> => {
    const response = await sirhApi.get<StateMachine>(
      '/sirh/leave/state-machine'
    );
    return unwrap(response.data);
  },

  /** Décompte sans rien écrire — appelé pendant la saisie. */
  previewLeave: async (
    payload: LeaveRequestPayload
  ): Promise<LeaveCounting> => {
    const response = await sirhApi.post<LeaveCounting>(
      '/sirh/leave/preview',
      payload
    );
    return unwrap(response.data);
  },

  createLeave: async (payload: LeaveRequestPayload): Promise<LeaveRequest> => {
    const response = await sirhApi.post<LeaveRequest>(
      '/sirh/leave/requests',
      payload
    );
    return unwrap(response.data);
  },

  submitLeave: async (
    uuid: string,
    expectedVersion: number
  ): Promise<LeaveRequest> => {
    const response = await sirhApi.post<LeaveRequest>(
      `/sirh/leave/requests/${uuid}/submit`,
      { expectedVersion }
    );
    return unwrap(response.data);
  },

  cancelLeave: async (
    uuid: string,
    expectedVersion: number
  ): Promise<LeaveRequest> => {
    const response = await sirhApi.post<LeaveRequest>(
      `/sirh/leave/requests/${uuid}/cancel`,
      { expectedVersion }
    );
    return unwrap(response.data);
  },

  decideLeave: async (
    uuid: string,
    payload: {
      expectedVersion: number;
      decision: 'approuve' | 'refuse';
      deciderEmploymentUuid: string;
      comment?: string;
    }
  ): Promise<LeaveRequest> => {
    const response = await sirhApi.post<LeaveRequest>(
      `/sirh/leave/requests/${uuid}/decide`,
      payload
    );
    return unwrap(response.data);
  },

  restorePaidLeaveOverlap: async (
    uuid: string,
    payload: { notifiedAt: string; evidenceReference: string }
  ): Promise<RestoredPaidLeaveOverlap> => {
    const response = await sirhApi.post<RestoredPaidLeaveOverlap>(
      `/sirh/leave/requests/${uuid}/restore-paid-leave-overlap`,
      payload
    );
    return unwrap(response.data);
  },

  /** File d'un responsable : uniquement son périmètre, à la date demandée. */
  managerInbox: async (
    managerEmploymentUuid: string,
    onDate?: string
  ): Promise<LeaveRequest[]> => {
    const response = await sirhApi.get<LeaveRequest[]>(
      `/sirh/leave/inbox/${managerEmploymentUuid}`,
      { params: onDate ? { onDate } : undefined }
    );
    return unwrap(response.data);
  },

  checkVariables: async (periodUuid: string): Promise<TransmissionCheck> => {
    const response = await sirhApi.get<TransmissionCheck>(
      `/sirh/variables/periods/${periodUuid}/check`
    );
    return unwrap(response.data);
  },

  openVariablePeriod: async (
    folderUuid: string,
    period: string
  ): Promise<VariablePeriod> => {
    const response = await sirhApi.post<VariablePeriod>(
      '/sirh/variables/periods',
      {
        folderUuid,
        period,
      }
    );
    return unwrap(response.data);
  },

  freezeVariablePeriod: async (
    periodUuid: string,
    expectedVersion: number
  ): Promise<VariablePeriod> => {
    const response = await sirhApi.post<VariablePeriod>(
      `/sirh/variables/periods/${periodUuid}/freeze`,
      { expectedVersion }
    );
    return unwrap(response.data);
  },

  transmitVariablePeriod: async (
    periodUuid: string,
    expectedVersion: number
  ) => {
    const response = await sirhApi.post(
      `/sirh/variables/periods/${periodUuid}/transmit`,
      { expectedVersion }
    );
    return unwrap(response.data);
  },

  reopenVariablePeriod: async (
    periodUuid: string,
    expectedVersion: number,
    reason: string
  ): Promise<VariablePeriod> => {
    const response = await sirhApi.post<VariablePeriod>(
      `/sirh/variables/periods/${periodUuid}/reopen`,
      { expectedVersion, reason }
    );
    return unwrap(response.data);
  },
};
